/**
 * Disparos de e-mail: escreva, escolha o publico pos-fato e dispare.
 *
 * Hierarquia, de cima para baixo:
 *  1. Identidade e acoes (automacoes, descadastrados, nova campanha).
 *  2. Barra de status do motor (provedor, ultimo envio, agendados vencidos,
 *     dominios autenticados) com o teste de entrega detalhado.
 *  3. Retorno do e-mail com a variacao contra o periodo anterior, o caminho
 *     do aceito ao pago e o anel por status.
 *  4. Tabela de campanhas com busca, empresa, status, ordenacao e "ver mais".
 *
 * Decisoes nao obvias:
 *  - UM recurso (`/api/admin/email-campaigns`) para campanhas, dominios,
 *    automacoes e descadastros: a tela e uma foto do canal, e tres rotas com
 *    tres erros deixariam a barra de status sem sentido.
 *  - A lista cruza TODAS as empresas (a casa dispara em nome delas); a
 *    empresa em foco do cabecalho so serve de padrao para o compositor e para
 *    o teste de entrega.
 *  - O compositor e montado com `key` nova a cada abertura para o estado do
 *    formulario nao vazar entre campanhas; editar grava por `ajustar`, criar
 *    grava por `adicionar`.
 *  - Enquanto o provedor for `a_definir`, "Disparar" cria a campanha como
 *    "enviando" mesmo assim: e a previa da experiencia, e a barra de status
 *    deixa claro que nada sai de verdade.
 */

import { useCallback, useEffect, useMemo, useState } from 'react';
import { ErrorPanel, RefreshingDot, SimulatedNotice } from '../../../components/PageState';
import { Badge, Button, HeroBanner } from '../../../components/ui';
import { useApiResource } from '../../../lib/api';
import { useCompany } from '../../../lib/companyContext';
import { formatCount, formatDate, formatDateTime } from '../../../lib/format';
import { empresaPorId } from '../../../lib/mock/catalogo';
import { describeSelection } from '../../../lib/period';
import { usePeriod } from '../../../lib/periodContext';
import { fetchEmailCampaigns } from '../api';
import { instanteAgendado, montarCampanha, type FormularioCampanhaEmail } from '../campanhaEmail';
import { BarraStatusEmail } from '../components/BarraStatusEmail';
import { EsqueletoDisparos } from '../components/EsqueletoDisparos';
import { KpisEmail } from '../components/KpisEmail';
import { ModalAutomacoesEmail } from '../components/ModalAutomacoesEmail';
import { ModalConfirmarExclusao } from '../components/ModalConfirmarExclusao';
import { ModalDescadastrados } from '../components/ModalDescadastrados';
import { ModalNovaCampanhaEmail } from '../components/ModalNovaCampanhaEmail';
import { ModalRelatorioEmail } from '../components/ModalRelatorioEmail';
import { ModalTesteEntrega } from '../components/ModalTesteEntrega';
import { TabelaDeCampanhasEmail } from '../components/TabelaDeCampanhasEmail';
import { VoltarPara } from '../components/VoltarPara';
import { segmentosDaEmpresa } from '../mock';
import { avancarCampanha, campanhaEnviando } from '../progresso';
import type { EmailCampaign, SegmentKey } from '../types';
import { useColecaoLocal } from '../useColecaoLocal';
import { useFeedbackTemporario } from '../useFeedbackTemporario';
import { useProgressoSimulado } from '../useProgressoSimulado';

const SEM_CAMPANHAS: readonly EmailCampaign[] = [];

interface Compositor {
  readonly versao: number;
  readonly inicial: EmailCampaign | null;
}

/**
 * Publico estimado da campanha na previa: o segmento pos-fato da empresa
 * escolhida. Fora do catalogo (ou segmento que a empresa nao tem, como
 * expirados sem o funil ligado) e ZERO, nunca um numero de enfeite: o
 * compositor bloqueia o disparo para publico vazio. Em producao vem do
 * servidor junto com o contexto da empresa.
 */
function destinatariosEstimados(companyId: string, segmentKey: SegmentKey): number {
  const empresa = empresaPorId(companyId);
  if (empresa === null) {
    return 0;
  }
  return segmentosDaEmpresa(empresa).find((s) => s.key === segmentKey)?.estimatedCount ?? 0;
}

export function DisparosEmail(): JSX.Element {
  const { company, companies } = useCompany();
  const { range, selection } = usePeriod();

  useEffect(() => {
    document.title = 'Disparos de E-mail · Converte Fácil';
  }, []);

  const buscar = useCallback((signal: AbortSignal) => fetchEmailCampaigns(range, signal), [range]);
  const { state, reload } = useApiResource(buscar, 30_000);

  const colecao = useColecaoLocal<EmailCampaign>();
  const base = state.status === 'success' ? state.data.campaigns : SEM_CAMPANHAS;
  const aplicarLocais = colecao.aplicar;
  const campanhas = useMemo(() => aplicarLocais(base), [aplicarLocais, base]);
  useProgressoSimulado(campanhas, campanhaEnviando, avancarCampanha, colecao.ajustar);

  const [automacoesLocais, setAutomacoesLocais] = useState<Readonly<Record<string, boolean>>>({});
  const [automacaoSalva, setAutomacaoSalva] = useState<string | null>(null);
  const [modalAutomacoes, setModalAutomacoes] = useState(false);
  const [modalDescadastrados, setModalDescadastrados] = useState(false);
  const [modalTeste, setModalTeste] = useState(false);
  const [compositor, setCompositor] = useState<Compositor | null>(null);
  const [relatorioId, setRelatorioId] = useState<string | null>(null);
  const [excluirId, setExcluirId] = useState<string | null>(null);
  const { mensagem, mostrar } = useFeedbackTemporario();

  useEffect(() => {
    if (automacaoSalva === null) {
      return undefined;
    }
    const timer = window.setTimeout(() => setAutomacaoSalva(null), 1_800);
    return () => window.clearTimeout(timer);
  }, [automacaoSalva]);

  const automacoes = useMemo(
    () => (state.status === 'success' ? state.data.automations.map((a) => ({ ...a, enabled: automacoesLocais[a.id] ?? a.enabled })) : []),
    [state, automacoesLocais],
  );

  const abrirCompositor = (inicial: EmailCampaign | null): void => {
    setCompositor((prev) => ({ versao: (prev?.versao ?? 0) + 1, inicial }));
  };

  const gravar = (form: FormularioCampanhaEmail, acao: 'rascunho' | 'disparar'): void => {
    const existente = compositor?.inicial ?? null;
    const status = acao === 'rascunho' ? 'rascunho' : instanteAgendado(form) === null ? 'enviando' : 'agendada';
    const total = destinatariosEstimados(form.companyId, form.segmentKey);
    if (existente === null) {
      const id = `SIMULADO-email-local-${String(colecao.locais.length + 1).padStart(3, '0')}`;
      colecao.adicionar(montarCampanha(form, status, id, total, null));
    } else {
      const atualizada = montarCampanha(form, status, existente.id, total, existente);
      colecao.ajustar(existente.id, {
        subject: atualizada.subject,
        preheader: atualizada.preheader,
        body: atualizada.body,
        ctaLabel: atualizada.ctaLabel,
        ctaUrl: atualizada.ctaUrl,
        segmentKey: atualizada.segmentKey,
        status: atualizada.status,
        scheduledFor: atualizada.scheduledFor,
        totalRecipients: atualizada.totalRecipients,
      });
    }
    setCompositor(null);
    const assunto = form.subject.trim();
    mostrar(
      status === 'rascunho'
        ? `Rascunho "${assunto}" salvo.`
        : status === 'agendada'
          ? `"${assunto}" agendada para ${formatDateTime(instanteAgendado(form))}.`
          : `"${assunto}" disparada: acompanhe o progresso na lista. Os pagamentos aparecem em até 7 dias.`,
    );
  };

  const excluir = (): void => {
    if (excluirId === null) {
      return;
    }
    const alvo = campanhas.find((c) => c.id === excluirId);
    // Defesa em profundidade: so rascunho ou agendada (a tabela ja desabilita o resto).
    if (alvo === undefined || (alvo.status !== 'rascunho' && alvo.status !== 'agendada')) {
      setExcluirId(null);
      return;
    }
    colecao.remover(excluirId);
    setExcluirId(null);
    mostrar(`Campanha "${alvo?.subject ?? ''}" excluída.`);
  };

  const relatorio = relatorioId === null ? null : campanhas.find((c) => c.id === relatorioId) ?? null;
  const alvoExclusao = excluirId === null ? null : campanhas.find((c) => c.id === excluirId)?.subject ?? null;
  const periodoTexto = describeSelection(selection);

  return (
    <section aria-labelledby="titulo-da-pagina" className="flex flex-col gap-6">
      <HeroBanner
        icon="email"
        tone="accent"
        backTo={<VoltarPara to="/admin/disparos" label="Disparos" />}
        title="Disparos de E-mail"
        badges={
          <>
            <Badge tone="brand" dot>
              Administração
            </Badge>
            <Badge tone="neutral" icon="escudo">
              Só depois do fato
            </Badge>
          </>
        }
        description={`Todas as empresas · ${periodoTexto}. Escreva, escolha um público que já abandonou ou venceu e dispare.`}
        aside={
          <>
            <RefreshingDot active={state.status === 'success' && state.refreshing} />
            <Button variant="secondary" size="sm" icon="raio" onClick={() => setModalAutomacoes(true)} disabled={state.status !== 'success'}>
              Automações
            </Button>
            <Button variant="secondary" size="sm" icon="saida" onClick={() => setModalDescadastrados(true)} disabled={state.status !== 'success'}>
              Descadastrados
            </Button>
            <Button variant="primary" size="sm" icon="mais" onClick={() => abrirCompositor(null)} disabled={state.status !== 'success'}>
              Nova campanha
            </Button>
          </>
        }
      />

      {state.status === 'loading' ? <EsqueletoDisparos variante="email" /> : null}
      {state.status === 'error' ? <ErrorPanel title="Não foi possível carregar as campanhas de e-mail" error={state.error} onRetry={reload} /> : null}

      {state.status === 'success' ? (
        <>
          {state.simulated ? (
            <SimulatedNotice>Campanhas, domínios, automações e descadastros são fictícios e fixos, com tarifa de exemplo. Criar, editar, disparar e excluir funcionam só nesta tela.</SimulatedNotice>
          ) : null}

          {mensagem === null ? null : (
            <p role="status" className="flex flex-wrap items-center gap-2 text-sm text-ink">
              <Badge tone="warning" dot>
                Simulado na prévia
              </Badge>
              {mensagem}
            </p>
          )}

          <BarraStatusEmail data={{ ...state.data, sendingNow: campanhas.filter(campanhaEnviando).length }} onTesteDeEntrega={() => setModalTeste(true)} />

          <KpisEmail campanhas={campanhas} anterior={state.data.previous} periodoAnterior={state.data.previousPeriod} porMilCents={state.data.perThousandCents} periodoTexto={periodoTexto} />

          <TabelaDeCampanhasEmail
            campanhas={campanhas}
            companies={companies}
            onNova={() => abrirCompositor(null)}
            acoes={{
              onRelatorio: setRelatorioId,
              onEditar: (id) => abrirCompositor(campanhas.find((c) => c.id === id) ?? null),
              onExcluir: setExcluirId,
            }}
          />

          <p className="text-xs text-ink-subtle">
            Período de {formatDate(state.data.periodStart)} a {formatDate(state.data.periodEnd)} ({periodoTexto}) · {formatCount(companies.length)} {companies.length === 1 ? 'empresa' : 'empresas'} ·
            nenhuma campanha fala com quem ainda não abandonou ou venceu.
          </p>

          <ModalAutomacoesEmail
            open={modalAutomacoes}
            onClose={() => setModalAutomacoes(false)}
            automacoes={automacoes}
            salvoId={automacaoSalva}
            onToggle={(id, enabled) => {
              setAutomacoesLocais((prev) => ({ ...prev, [id]: enabled }));
              setAutomacaoSalva(id);
            }}
          />
          <ModalDescadastrados open={modalDescadastrados} onClose={() => setModalDescadastrados(false)} descadastrados={state.data.unsubscribes} total={state.data.unsubscribeTotal} companies={companies} />
          {modalTeste ? (
            <ModalTesteEntrega open onClose={() => setModalTeste(false)} companies={companies} companyIdPadrao={company.id} domains={state.data.domains} campanhas={campanhas} />
          ) : null}
          <ModalRelatorioEmail campanha={relatorio} porMilCents={state.data.perThousandCents} onClose={() => setRelatorioId(null)} />
        </>
      ) : null}

      {compositor === null ? null : (
        <ModalNovaCampanhaEmail
          key={compositor.versao}
          open
          onClose={() => setCompositor(null)}
          companies={companies}
          companyIdPadrao={company.id}
          inicial={compositor.inicial}
          domains={state.status === 'success' ? state.data.domains : []}
          destinatarios={destinatariosEstimados}
          onSalvarRascunho={(form) => gravar(form, 'rascunho')}
          onDisparar={(form) => gravar(form, 'disparar')}
        />
      )}
      <ModalConfirmarExclusao alvo={alvoExclusao} onCancel={() => setExcluirId(null)} onConfirm={excluir} />
    </section>
  );
}
